import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { useMutation, useQuery } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { PaymentsCreateOrderAttributes } from "~/api/Api.ts";
import { apiClient } from "~/api/ApiClient.ts";
import { CommunityLinksPackage } from "~/components/services/communityLinksPackage/CommunityLinksPackage";
import { usePendingOrders } from "~/components/services/hooks/useOrdersWithPermissions.ts";
import { PendingOrder } from "~/components/services/orders/PendingOrder.tsx";
import Modal from "~/components/ui/modal/Modal.tsx";
import Spinner from "~/components/ui/spinner/Spinner.tsx";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { stripePromise } from "~/helpers/load_stripe.ts";
import { messageFromError } from "~/helpers/messageFromError.ts";
import { tryCatch } from "~/helpers/tryCatch";
import { usePageTitle } from "~/hooks/usePageTitle";
import { useStripeSession } from "~/hooks/useStripeSession.tsx";

import { SiteDomain } from "./ConciergeInstallationPage";
import styles from "./ServicesListPage.module.scss";

const CommunityLinksPage = () => {
  usePageTitle(PAGE_TITLES.communityLinksService);

  const { data: products, isLoading } = useQuery({
    queryKey: ["services", "products-list"],
    queryFn: async () => {
      const response = await apiClient.billing.productsList({
        "page[size]": 10,
        "page[number]": 1,
        s: "created_at ASC",
        q: {
          kind_eq: "product",
        },
      });

      return response.data?.["payments/products"];
    },
  });

  const {
    clientSecret,
    isModalOpen,
    isCreatingSession,
    openSession,
    closeModal,
  } = useStripeSession({
    sessionType: "pay",
    onSuccess: () => {},
    onError: () => {},
  });

  const { orders: pendingOrders, refetch: refetchPendingOrders } =
    usePendingOrders();

  const { data: siteDomains } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      const response = await apiClient.sites.sitesList({
        "page[size]": 1000,
        "page[number]": 1,
        s: "created_at ASC",
      });

      return response?.data?.sites?.map((site) => ({
        label: site.domain,
        value: site.id.toString(),
      }));
    },
  });

  const { mutate: createOrder, isPending: isCreatingOrderPending } =
    useMutation({
      mutationFn: async (order: PaymentsCreateOrderAttributes) => {
        const result = await tryCatch(apiClient.billing.ordersCreate(order));

        if (result.error) {
          enqueueSnackbar(
            messageFromError(result.error || "Something went wrong"),
            {
              variant: "error",
              persist: true,
            },
          );

          if (result.error.status === 403) {
            await refetchPendingOrders();
          }

          throw result.error;
        }

        return result.data;
      },
      onSuccess: async () => {
        await refetchPendingOrders();
        enqueueSnackbar("Order was successfully created", {
          variant: "success",
        });
        openSession();
      },
    });

  const pendingOrder = pendingOrders[0];

  const handleCloseModal = () => {
    closeModal();
    refetchPendingOrders();
  };

  // Match on the stable `template` key, not the display name — the Stripe
  // product name can change (it was renamed to "Community Links") while the
  // template stays "community".
  const product = products?.find((p) => p.template === "community");

  if (isLoading || !product) {
    return <Spinner />;
  }

  return (
    <>
      <div className={styles.page}>
        {pendingOrder && (
          <PendingOrder
            order={pendingOrder}
            refetchPendingOrders={refetchPendingOrders}
          />
        )}
        <h1 className={styles.title}>{PAGE_TITLES.communityLinksService}</h1>
        <CommunityLinksPackage
          product={product}
          targetSites={(siteDomains ?? []) as SiteDomain[]}
          createOrder={createOrder}
          isCreateOrderPending={isCreatingOrderPending}
        />
      </div>
      <Modal
        key="payOrder"
        isOpen={isModalOpen}
        closeModal={handleCloseModal}
        formId="cancelationForm"
        height="92%"
        width="80%"
        modalLabel={<h3></h3>}
        forceWhiteBackground
      >
        {isCreatingSession && <Spinner />}
        {clientSecret && stripePromise && (
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ clientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        )}
      </Modal>
    </>
  );
};

export default CommunityLinksPage;
